// Iteration 56 (Feb 28 2026) — Central helper for rendering the accurate
// subscription state on the Business Dashboard, Settings, and Billing page.
// Reads the same fields Stripe writes onto the user row via webhook:
//   subscription_status  (active | trialing | past_due | canceled | incomplete | ...)
//   cancel_at_period_end (bool, Stripe-side flag)
//   subscription_expires_at  (ISO date — Stripe's `current_period_end` OR
//                             `canceled_at`, whichever applies)
//   trial_end_at        (ISO date — Stripe's `trial_end`)
// Returns { tone, label, message, actionLabel, needsAttention } so the
// caller can render a status pill + a message the user actually understands.
export function subscriptionStatusView(user) {
  if (!user) return { tone: "muted", label: "No subscription", message: "", needsAttention: false };
  const status = String(user.subscription_status || "").toLowerCase();
  const cancelAtPeriodEnd = !!user.cancel_at_period_end;
  const expiresAt = user.subscription_expires_at ? new Date(user.subscription_expires_at) : null;
  const trialEndAt = user.trial_end_at ? new Date(user.trial_end_at) : null;
  const now = new Date();
  const fmt = (d) => (d instanceof Date && !isNaN(d)) ? d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";

  // Past due / incomplete / unpaid ==> big red warning.
  if (["past_due", "incomplete", "unpaid", "incomplete_expired"].includes(status)) {
    return {
      tone: "bad",
      label: "Payment issue",
      message: "Your last payment did not go through. Update your payment method in the Stripe portal to keep your listings live.",
      actionLabel: "Fix payment method",
      needsAttention: true,
    };
  }

  // Trialing — even if trial_end has passed, wait for Stripe to move to active/past_due before switching messaging.
  if (status === "trialing" && trialEndAt) {
    return {
      tone: "ok",
      label: "Free trial",
      message: `Your free trial ends on ${fmt(trialEndAt)}.`,
      actionLabel: "Manage subscription",
      needsAttention: false,
    };
  }

  // Active — either renewing or scheduled to cancel at period end.
  if (status === "active") {
    if (cancelAtPeriodEnd && expiresAt) {
      return {
        tone: "warn",
        label: "Cancelled at period end",
        message: `Your subscription has been cancelled and will remain active until ${fmt(expiresAt)}.`,
        actionLabel: "Manage subscription",
        needsAttention: false,
      };
    }
    if (expiresAt) {
      return {
        tone: "ok",
        label: "Active",
        message: `Your subscription will renew on ${fmt(expiresAt)}.`,
        actionLabel: "Manage subscription",
        needsAttention: false,
      };
    }
    return {
      tone: "ok",
      label: "Active",
      message: "Your subscription is active.",
      actionLabel: "Manage subscription",
      needsAttention: false,
    };
  }

  // Canceled — Stripe writes subscription_status=canceled once fully ended.
  // If expiresAt is in the future, treat as "cancelled but still active".
  if (status === "canceled" || status === "cancelled") {
    if (expiresAt && expiresAt > now) {
      return {
        tone: "warn",
        label: "Cancelled at period end",
        message: `Your subscription has been cancelled and will remain active until ${fmt(expiresAt)}.`,
        actionLabel: "Reactivate in Stripe",
        needsAttention: false,
      };
    }
    return {
      tone: "bad",
      label: "Cancelled",
      message: expiresAt ? `Your subscription ended on ${fmt(expiresAt)}.` : "Your subscription has ended.",
      actionLabel: "Restart subscription",
      needsAttention: true,
    };
  }

  // Everything else — no known active subscription.
  return {
    tone: "muted",
    label: "No subscription",
    message: "You don't have an active subscription yet. Subscribe to get discovered by customers.",
    actionLabel: "Subscribe",
    needsAttention: true,
  };
}


export function isSubscriptionActiveByStatus(user) {
  const v = subscriptionStatusView(user);
  return v.tone === "ok" || (v.tone === "warn" && v.label === "Cancelled at period end");
}
